import { useEffect, useState, useRef, type ChangeEvent } from "react";
import AppLayout from "@/components/feature/AppLayout";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";
import Select from "@/components/base/Select";
import ConfirmDialog from "@/components/feature/ConfirmDialog";
import { useToast } from "@/store/ToastContext";
import { settingsApi, expenseApi, exportExpenses } from "@/services/api";
import { CURRENCIES } from "@/utils/currency";
import type { AppSettings, Expense } from "@/models/types";

const DEFAULT_SETTINGS: AppSettings = {
  id: "settings",
  currency: "USD",
  language: "es",
  theme: "light",
  userName: "Usuario",
  monthlyBudget: 0,
  notifications: {
    weeklySummary: true,
    budgetAlert: true,
  },
};
function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-5">
      <h3 className="text-base font-heading font-semibold text-foreground-950">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-foreground-500 mt-0.5 mb-4">{description}</p>
      )}
      {!description && <div className="mb-4" />}
      {children}
    </div>
  );
}

function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between w-full py-2 cursor-pointer"
    >
      <span className="text-sm text-foreground-800">{label}</span>
      <span
        className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
          checked ? "bg-primary-500" : "bg-secondary-300"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
            checked ? "translate-x-5" : ""
          }`}
        />
      </span>
    </button>
  );
}

export default function Settings() {
  const { showToast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState(DEFAULT_SETTINGS.userName);
  const [budget, setBudget] = useState(String(DEFAULT_SETTINGS.monthlyBudget));
  const [logoutOpen, setLogoutOpen] = useState(false);

  // Cargar configuración y gastos
 useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsData, expensesData] = await Promise.all([
          settingsApi.get(),
          expenseApi.list(),
        ]);
        const merged = { ...DEFAULT_SETTINGS, ...settingsData };
        setSettings(merged);
        setExpenses(expensesData);
        setName(merged.userName || DEFAULT_SETTINGS.userName);
        setBudget(String(merged.monthlyBudget ?? DEFAULT_SETTINGS.monthlyBudget));
        showToast("Configuración cargada", "success");
      } catch (error) {
        console.error("Error cargando configuración:", error);
        showToast("Usando configuración por defecto.", "info");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  const updateSettings = async (updates: Partial<AppSettings>) => {
    try {
      const updated = await settingsApi.update(updates);
      const merged = { ...DEFAULT_SETTINGS, ...updated };
      setSettings(merged);
      if (updates.userName !== undefined) setName(merged.userName);
      if (updates.monthlyBudget !== undefined) setBudget(String(merged.monthlyBudget));
    } catch (error) {
      console.error("Error actualizando configuración:", error);
      showToast("No se pudo guardar la configuración.", "error");
    }
  };

  const saveProfile = async () => {
    const budgetNum = Number(budget);
    const updates: Partial<AppSettings> = { userName: name.trim() || "Usuario" };
    if (!isNaN(budgetNum) && budgetNum >= 0) {
      updates.monthlyBudget = budgetNum;
    }
    await updateSettings(updates);
    showToast("Configuración guardada.");
  };

  const handleExport = () => {
    if (!settings) return;
    exportExpenses(expenses, settings);
    showToast("Exportación generada.");
  };

  const handleImport = (e: ChangeEvent<HTMLInputElement>) => {
    // La importación podría implementarse contra un endpoint futuro.
    // Por ahora se mantiene una versión local opcional.
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(String(reader.result));
        // Aquí se podría llamar a un endpoint de importación.
        // Por simplicidad, solo mostramos un mensaje.
        showToast("Importación no implementada en esta versión.", "info");
      } catch {
        showToast("Error al importar: archivo no válido.", "error");
      }
    };
    reader.readAsText(file);
  };

  if (loading || !settings) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-4">
        <Section title="Perfil del usuario">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-bold">
              {settings.userName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-medium text-foreground-950">{settings.userName}</p>
              <p className="text-sm text-foreground-500">Tu cuenta personal</p>
            </div>
          </div>
          <Input
            label="Nombre"
            name="userName"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Section>

        <Section title="Moneda" description="Selecciona la moneda de tus gastos.">
          <Select
            value={settings.currency}
            onChange={(e) => updateSettings({ currency: e.target.value })}
            options={CURRENCIES.map((c) => ({
              value: c.code,
              label: `${c.code} — ${c.name}`,
            }))}
          />
        </Section>

        <Section
          title="Presupuesto mensual"
          description="Define un límite de gasto mensual para ver tu progreso."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <Input
              label="Presupuesto"
              name="budget"
              type="number"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              icon="ri-wallet-3-line"
            />
            <Button onClick={saveProfile}>Guardar cambios</Button>
          </div>
        </Section>

     
        <Section
          title="Tema"
          description="Cambia entre modo claro y oscuro."
        >
          <Switch
            label="Modo oscuro"
            checked={settings.theme === "dark"}
            onChange={(v) => updateSettings({ theme: v ? "dark" : "light" })}
          />
        </Section>

        <Section
          title="Datos"
          description="Exporta o importa tus gastos en formato JSON."
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              icon="ri-download-line"
              onClick={handleExport}
            >
              Exportar gastos
            </Button>
            <Button
              variant="outline"
              icon="ri-upload-line"
              onClick={() => fileRef.current?.click()}
            >
              Importar datos
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={handleImport}
            />
          </div>
        </Section>

        
      </div>

    
    </AppLayout>
  );
}